import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { DEMO_ACCOUNTS } from '../../constants';

const USE_API = import.meta.env.VITE_USE_API === 'true';

const ROLE_META: Record<string, { label: string; desc: string; badgeColor: string; badgeBg: string }> = {
  admin:   { label: 'Admin',   desc: 'Full access',       badgeColor: '#92400E', badgeBg: '#FEF3C7' },
  manager: { label: 'Manager', desc: 'Team + reports',    badgeColor: '#1E40AF', badgeBg: '#DBEAFE' },
  rep:     { label: 'Rep',     desc: 'Own deals only',    badgeColor: '#166534', badgeBg: '#DCFCE7' },
};

export default function LoginScreen() {
  const { login, loginWithCredentials } = useApp();
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const doLogin = async () => {
    if (!email || !pwd) { setError('Email and password are required'); return; }
    setLoading(true); setError('');
    try {
      if (USE_API) {
        await loginWithCredentials(email, pwd);
      } else {
        const user = DEMO_ACCOUNTS.find(u => u.email === email && u.pwd === pwd);
        if (user) login(user);
        else setError('Invalid credentials. Try a demo account below.');
      }
    } catch (e) {
      setError((e as Error).message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0F1B2D 0%, #1A2D45 50%, #0F1B2D 100%)', padding: '24px 16px' }}>
      <div style={{ position: 'fixed', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '28px 28px', pointerEvents: 'none' }} />

      {/* Brand header above card */}
      <div style={{ textAlign: 'center', marginBottom: '28px' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: '#C8973A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
          </div>
          <span style={{ fontSize: '24px', fontWeight: '800', color: 'white', fontFamily: "'DM Serif Display', serif", letterSpacing: '-0.3px' }}>SalesBook</span>
        </div>
        <div style={{ fontSize: '13px', color: '#94A3B8', marginTop: '8px' }}>Sign in to your workspace</div>
      </div>

      {/* Login card */}
      <div style={{ background: 'white', borderRadius: '20px', padding: '36px', width: '100%', maxWidth: '420px', boxShadow: '0 32px 80px rgba(0,0,0,0.5)', position: 'relative' }}>
        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '6px' }}>Email</label>
          <input
            type="email" value={email}
            onChange={e => { setEmail(e.target.value); setError(''); }}
            placeholder="you@company.com"
            onKeyDown={e => e.key === 'Enter' && doLogin()}
            style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #E2E8F0', borderRadius: '10px', fontFamily: 'inherit', fontSize: '14px', marginBottom: '16px', outline: 'none', boxSizing: 'border-box', color: '#1A202C' }}
          />
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#4A5568', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '6px' }}>Password</label>
          <input
            type="password" value={pwd}
            onChange={e => { setPwd(e.target.value); setError(''); }}
            placeholder="••••••••"
            onKeyDown={e => e.key === 'Enter' && doLogin()}
            style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #E2E8F0', borderRadius: '10px', fontFamily: 'inherit', fontSize: '14px', marginBottom: '8px', outline: 'none', boxSizing: 'border-box', color: '#1A202C' }}
          />
          {error && <div style={{ fontSize: '12px', color: '#EF4444', marginBottom: '8px' }}>{error}</div>}
          <button
            onClick={doLogin} disabled={loading}
            style={{ width: '100%', padding: '13px', borderRadius: '10px', background: loading ? '#4A5568' : '#0F1B2D', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '15px', fontWeight: '600', cursor: loading ? 'wait' : 'pointer', marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'background .15s' }}>
            {loading ? (
              <><i className="ti ti-loader-2" style={{ animation: 'spin 1s linear infinite', fontSize: '16px' }} /> Signing in…</>
            ) : (
              <><i className="ti ti-login-2" style={{ fontSize: '17px' }} /> Sign in</>
            )}
          </button>
        </div>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '24px 0 16px' }}>
          <div style={{ flex: 1, height: '1px', background: '#E2E8F0' }} />
          <span style={{ fontSize: '12px', color: '#94A3B8' }}>Demo accounts — click to fill</span>
          <div style={{ flex: 1, height: '1px', background: '#E2E8F0' }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {DEMO_ACCOUNTS.map(a => {
            const meta = ROLE_META[a.role] ?? ROLE_META.rep;
            return (
              <button
                key={a.email}
                onClick={() => { setEmail(a.email); setPwd(a.pwd); setError(''); }}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #E2E8F0', background: 'white', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', transition: 'border-color .15s' }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = '#CBD5E0')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = '#E2E8F0')}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: a.color, color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{a.ini}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#1A202C' }}>{a.name}</div>
                  <div style={{ fontSize: '11px', color: '#718096' }}>{meta.label} · {meta.desc}</div>
                </div>
                <span style={{ fontSize: '11px', fontWeight: '600', color: meta.badgeColor, background: meta.badgeBg, padding: '3px 9px', borderRadius: '20px', flexShrink: 0 }}>{meta.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Bottom client note */}
      <div style={{ marginTop: '20px', maxWidth: '420px', width: '100%', background: 'rgba(255,255,255,0.06)', borderRadius: '12px', padding: '13px 16px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <i className="ti ti-link" style={{ color: '#94A3B8', fontSize: '15px', marginTop: '1px', flexShrink: 0 }} />
        <p style={{ margin: 0, fontSize: '12px', color: '#94A3B8', lineHeight: '1.5' }}>
          <strong style={{ color: '#CBD5E0' }}>Clients do not log in.</strong> They get a private share link from their rep — no account needed.
        </p>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
